import { useAuthStore } from '@container/Auth';
import { useNotificationStore } from '@container/NotificationProvider';
import {
  SHOP_SELECTED_KEY,
  SHOP_STATUS_ACTIVE,
  SHOP_STATUS_EXPIRED,
  SHOP_STATUS_INACTIVE,
  SHOP_STATUS_PENDING,
  SHOP_STATUS_REVOKE,
} from '@helpers/constants';
import { titleCase } from '@helpers/utils';
import { navigate } from '@reach/router';
import { ActionList, Badge, Button, Popover, TextStyle } from '@shopify/polaris';
import { ChevronDownMinor, ChevronUpMinor, MobileVerticalDotsMajor } from '@shopify/polaris-icons';
import { Fragment, useCallback, useState } from 'react';
import { useMedia } from '@shopify/react-hooks';

export const STATUS_MAPPING = {
  [SHOP_STATUS_ACTIVE]: 'success',
  [SHOP_STATUS_INACTIVE]: '',
  [SHOP_STATUS_REVOKE]: 'critical',
  [SHOP_STATUS_PENDING]: 'attention',
  [SHOP_STATUS_EXPIRED]: '',
};

const ShopItem = ({ store, onDelete, onEdit, onAddPurchase, onUpdateStatus, index, storesDisplay }) => {
  const [collapsible, setCollapsible] = useState({});
  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((active) => !active), []);
  const { showConfirm } = useNotificationStore();
  const { currentUser } = useAuthStore();

  const { status, _id, domain, license, shop, name, appType, expiresAt } = store;

  const isSmallScreen = useMedia('(max-width:650px)');
  const isNarrowScreenWithNav = useMedia('(min-width:768px) and (max-width: 860px)');

  const handleDelete = async () => {
    await onDelete(_id);
  };

  const handleOpenNewRow = (id) => {
    setCollapsible((prevState) => ({
      ...prevState,
      [id]: !(prevState[id] ?? false),
    }));
  };

  const handleUpdateStatus = async (newStatus) => {
    if (newStatus === SHOP_STATUS_ACTIVE) {
      if (onUpdateStatus) await onUpdateStatus(_id, newStatus);
      toggleActive();
    } else {
      showConfirm({
        title: `Deactivate this store `,
        confirmButtonText: 'Deactivate',
        danger: true,
        message: `Are you sure you want to deactivate this store.`,
        onConfirm: async () => {
          if (onUpdateStatus) await onUpdateStatus(_id, newStatus);
          if (localStorage.getItem(SHOP_SELECTED_KEY) === shop) navigate('/select-store');
          toggleActive();

          return Promise.resolve();
        },
      });
    }
  };

  const actionList = [
    {
      content: 'Edit',
      onAction: () => onEdit({ ...store }),
    },
    !license?.code
      ? {
          content: 'Add purchase code',
          onAction: () => onAddPurchase(shop),
        }
      : null,
    status === 'active'
      ? {
          content: 'Deactivate',
          onAction: () => handleUpdateStatus(SHOP_STATUS_INACTIVE),
          destructive: true,
        }
      : null,
    status === 'inactive' && appType === 'foxhome'
      ? {
          content: 'Activate',
          onAction: () => handleUpdateStatus(SHOP_STATUS_ACTIVE),
        }
      : null,
    [SHOP_STATUS_REVOKE, SHOP_STATUS_INACTIVE, SHOP_STATUS_EXPIRED, SHOP_STATUS_PENDING].includes(status)
      ? {
          content: 'Delete',
          onAction: handleDelete,
          destructive: true,
        }
      : null,
  ].filter(Boolean);

  return (
    <Fragment key={_id}>
      <tr>
        <td>
          <Badge status={STATUS_MAPPING[status]}>{titleCase(status)}</Badge>
        </td>
        <td>
          <div style={{ cursor: 'pointer' }} onClick={() => window.open(`https://${domain}`, '_blank')}>
            <div style={{ fontWeight: '500' }}>
              {name} {appType && <Badge>{appType}</Badge>}
            </div>
            <TextStyle variation={'subdued'}>{domain}</TextStyle>
          </div>
        </td>
        {isSmallScreen || isNarrowScreenWithNav ? null : (
          <td>
            {license?.code ? (
              <code>{license?.code}</code>
            ) : (
              <Button plain onClick={() => onAddPurchase(shop)}>
                Add purchase code
              </Button>
            )}
          </td>
        )}
        {!currentUser?.isGuest ? (
          <>
            <td>
              <Popover active={active} activator={<Button plain onClick={toggleActive} icon={MobileVerticalDotsMajor} />} onClose={toggleActive}>
                <ActionList actionRole="menuitem" items={actionList} />
              </Popover>
            </td>
            {(isSmallScreen || isNarrowScreenWithNav) && (
              <td>
                {collapsible[index] ? (
                  <Button icon={ChevronUpMinor} onClick={() => handleOpenNewRow(index)} plain />
                ) : (
                  <Button icon={ChevronDownMinor} onClick={() => handleOpenNewRow(index)} plain />
                )}
              </td>
            )}
          </>
        ) : null}
      </tr>
      {(collapsible[index] && isSmallScreen) || (collapsible[index] && isNarrowScreenWithNav) ? (
        <tr>
          <td colSpan={4}>
            <div className="purchase-code__options">
              {license?.code ? (
                <span>
                  Purchase code: <code>{license?.code}</code>
                </span>
              ) : (
                <Button plain onClick={() => onAddPurchase(shop)}>
                  Add purchase code
                </Button>
              )}
            </div>
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
};

export default ShopItem;
